import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { components } from '@awj/api-client';

import { fetchAllPages } from './export.ts';
import { admin } from './queries.ts';

type Invite = components['schemas']['InviteStaffRequest'];

export function useSettings() {
  return useQuery({
    queryKey: ['settings'],
    queryFn: () => admin().settings(),
  });
}

/**
 * Saving one group.
 *
 * Settings decide shipping fees, VAT and whether guests may check out, so everything that reads
 * them is dropped too — the dashboard's figures and the storefront's cached copy alike. The API
 * clears its own cache; this clears the panel's.
 */
export function useSaveSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ group, values }: { group: string; values: Record<string, string | null> }) =>
      admin().saveSettings(group, values),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useStaff() {
  return useQuery({
    queryKey: ['staff'],
    queryFn: () => admin().staff(),
  });
}

function useStaffInvalidation() {
  const queryClient = useQueryClient();

  return () => queryClient.invalidateQueries({ queryKey: ['staff'] });
}

export function useInviteStaff() {
  const invalidate = useStaffInvalidation();

  return useMutation({
    mutationFn: (body: Invite) => admin().inviteStaff(body),
    onSuccess: invalidate,
  });
}

export function useChangeStaffRole() {
  const invalidate = useStaffInvalidation();

  return useMutation({
    mutationFn: ({ id, role }: { id: number; role: string }) => admin().changeStaffRole(id, role),
    onSuccess: invalidate,
  });
}

export function useSetStaffBlocked() {
  const invalidate = useStaffInvalidation();

  return useMutation({
    mutationFn: ({ id, blocked }: { id: number; blocked: boolean }) => admin().setStaffBlocked(id, blocked),
    onSuccess: invalidate,
  });
}

export interface AuditQuery {
  action?: string;
  actor?: string;
  page?: number;
}

/** The filters as the API takes them — shared by the trail and its export, so the two agree. */
function filters(query: AuditQuery) {
  return {
    action: query.action || undefined,
    actor: query.actor || undefined,
  };
}

export function useAudit(query: AuditQuery) {
  return useQuery({
    queryKey: ['audit', query],
    queryFn: () => admin().audit({ ...filters(query), page: query.page ?? 1, pageSize: 30 }),
  });
}

/** Every entry the filters match, for an export. */
export function allAuditEntries(query: AuditQuery) {
  return fetchAllPages((page, pageSize) => admin().audit({ ...filters(query), page, pageSize }));
}
